import { useState, useEffect } from "react"
import ApiError from "../../components/ApiError"
import { useMissingPathologyLogsStats } from "../../hooks/useMissingPathologyLogs"
import MissingPathologyStatsTable from "./components/MissingPathologyStatsTable"
import { generateFilter } from "../../utils"

const MissingPathologies = () => {
  const [sorting, setSorting] = useState([{ id: "count", desc: true }])
  const [filters, setFilters] = useState([])
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")

  const { data, isLoading, isError, error } = useMissingPathologyLogsStats({
    sortBy: sorting.map((s) => `${s.id}:${s.desc ? "DESC" : "ASC"}`).join(","),
    filters: generateFilter(filters) || {},
  })

  // Update filters when date range changes
  useEffect(() => {
    setFilters((prev) => {
      const otherFilters = prev.filter(
        (f) => f.id !== "dateFrom" && f.id !== "dateTo"
      )
      const newFilters = [...otherFilters]

      if (dateFrom) {
        newFilters.push({ id: "dateFrom", value: dateFrom })
      }
      if (dateTo) {
        newFilters.push({ id: "dateTo", value: dateTo })
      }

      return newFilters
    })
  }, [dateFrom, dateTo])

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <>
      <div className="px-4 py-2 flex justify-between items-start mt-2 gap-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">Missing Pathologies</h1>
          <div className="border-l border-gray-300 h-8 mx-2"></div>
          <div className="flex items-center gap-3 bg-white border border-gray-300 rounded-md px-3">
            <span className="text-sm font-medium text-gray-700">From:</span>
            <div className="relative">
              <input
                className="appearance-none px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="mm/dd/yyyy"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <span className="text-sm font-medium text-gray-700">To:</span>
            <div className="relative">
              <input
                className="appearance-none px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="mm/dd/yyyy"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            {(dateFrom || dateTo) && (
              <button
                className="px-2 py-1 mr-1 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md transition-colors"
                onClick={() => {
                  setDateFrom("")
                  setDateTo("")
                  setFilters((prev) =>
                    prev.filter((f) => f.id !== "dateFrom" && f.id !== "dateTo")
                  )
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-8">
          <div className="flex flex-col justify-center items-center gap-2 bg-blue-100 rounded-md px-6 py-2 border border-blue-200">
            <h3 className="text-xs font-bold text-blue-500 uppercase">
              Total Missing
            </h3>
            <h1 className="font-bold text-3xl">{data?.total || 0}</h1>
          </div>
          <div className="flex flex-col justify-center items-center gap-2 bg-gray-200 rounded-md px-6 py-2 border border-gray-300">
            <h3 className="text-xs font-bold text-gray-500 uppercase">
              Unique Types
            </h3>
            <h1 className="font-bold text-3xl">{data?.unique || 0}</h1>
          </div>
        </div>
      </div>
      <hr className="w-full my-4 border-gray-300" />
      <MissingPathologyStatsTable
        data={isLoading ? { stats: [] } : data}
        filters={filters}
        isLoading={isLoading}
        setFilters={setFilters}
        setSorting={setSorting}
        sorting={sorting}
      />
    </>
  )
}

export default MissingPathologies
